import Stepper from "@raiden/library/components/Stepper";
import useRouterBoolean from "@raiden/library/hooks/router/useRouterBoolean";
import useRouterNumber from "@raiden/library/hooks/router/useRouterNumber";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useHeader } from "../../hooks/useHeader";
import StepCaf from "./StepCaf";
import StepContacts from "./StepContacts";
import StepDocuments from "./StepDocuments";
import StepHolders from "./StepHolders";
import StepInformation from "./StepInformation";
import StepPayment from "./StepPayment";
import StepSubscriptions from "./StepSubscriptions";

export const WIZARD_SUBSCRIPTIONS_LOCAL_STORAGE_KEY_INVOICE_ID =
  "cslm-wizard-subscriptions-invoice-id";
export const WIZARD_SUBSCRIPTIONS_SESSIONS_STORAGE_KEY_PRESTATION_ID =
  "cslm-wizard-subscriptions-prestation-id";

/**
 * @typedef {"information" | "holders" | "contacts" | "caf" | "documents" | "subscriptions" | "payment"} WizardStepAlias
 */

const WIZARD_STEPS_VALUE_INFORMATION = "information";
const WIZARD_STEPS_VALUE_HOLDERS = "holders";
const WIZARD_STEPS_VALUE_CONTACTS = "contacts";
const WIZARD_STEPS_VALUE_CAF = "caf";
const WIZARD_STEPS_VALUE_DOCUMENTS = "documents";
const WIZARD_STEPS_VALUE_SUBSCRIPTIONS = "subscriptions";
const WIZARD_STEPS_VALUE_PAYMENT = "payment";

function Wizard() {
  const intl = useIntl();

  const { push } = useRouter();

  const stepIndex = useRouterNumber("step") ?? 0;

  const { headerHeight } = useHeader();

  const isPaymentError = useRouterBoolean("paymentError") ?? false;

  const [displayPaymentError, setDisplayPaymentError] =
    useState(isPaymentError);

  const steps = useMemo(() => {
    /** @type {import("@raiden/library/components/Stepper").StepperStep[]} */
    const steps = [
      {
        alias: WIZARD_STEPS_VALUE_INFORMATION,
        title: intl.formatMessage({
          defaultMessage: "Informations personnelles",
        }),
        isEnabled: () => stepIndex > 0,
      },
      {
        alias: WIZARD_STEPS_VALUE_HOLDERS,
        title: intl.formatMessage({ defaultMessage: "Comptes adhérents" }),
        isEnabled: () => stepIndex > 1,
      },
      {
        alias: WIZARD_STEPS_VALUE_CONTACTS,
        title: intl.formatMessage({
          defaultMessage: "Personnes autorisées",
        }),
        isEnabled: () => stepIndex > 2,
      },
      {
        alias: WIZARD_STEPS_VALUE_CAF,
        title: intl.formatMessage({ defaultMessage: "Situation familiale" }),
        isEnabled: () => stepIndex > 3,
      },
      {
        alias: WIZARD_STEPS_VALUE_DOCUMENTS,
        title: intl.formatMessage({ defaultMessage: "Justificatifs" }),
        isEnabled: () => stepIndex > 4,
      },
      {
        alias: WIZARD_STEPS_VALUE_SUBSCRIPTIONS,
        title: intl.formatMessage({
          defaultMessage: "Choix des adhésions annuelles",
        }),
        isEnabled: () => stepIndex > 5,
      },
      {
        alias: WIZARD_STEPS_VALUE_PAYMENT,
        title: intl.formatMessage({
          defaultMessage: "Récapitulatif et paiement",
        }),
        isEnabled: () => stepIndex > 6,
      },
    ];
    return steps;
  }, [intl, stepIndex]);

  const setStepIndex = useCallback(
    /**
     * @param {number} index
     * @param {{ preservePaymentError?: boolean }} [options]
     */
    (index, options) => {
      if (!options?.preservePaymentError) {
        setDisplayPaymentError(false);
      }
      push(
        generateFrontPath({
          id: "@account.dashboard",
          query: { step: index },
        }),
        undefined,
        { shallow: true },
      );
    },
    [push],
  );

  const moveToStep = useCallback(
    /**
     * @param {WizardStepAlias} alias
     */
    (alias) => {
      setDisplayPaymentError(false);
      const stepIndex = steps.findIndex((step) => step.alias === alias);
      if (stepIndex !== -1) {
        setStepIndex(stepIndex);
      }
    },
    [setStepIndex, steps],
  );

  const nextStep = useCallback(() => {
    setDisplayPaymentError(false);
    setStepIndex(Math.min(stepIndex + 1, steps.length - 1));
  }, [setStepIndex, stepIndex, steps.length]);

  // focus step payment on payment error
  useEffect(() => {
    if (isPaymentError && stepIndex !== 6) {
      setStepIndex(6, { preservePaymentError: true });
    }
  }, [isPaymentError, setStepIndex, stepIndex]);

  return (
    <>
      <Stepper
        steps={steps}
        stepIndex={stepIndex}
        setStepIndex={setStepIndex}
        scrollMarginTop={headerHeight}>
        {stepIndex === 0 && <StepInformation nextStep={nextStep} />}

        {stepIndex === 1 && <StepHolders nextStep={nextStep} />}

        {stepIndex === 2 && <StepContacts nextStep={nextStep} />}

        {stepIndex === 3 && <StepCaf nextStep={nextStep} />}

        {stepIndex === 4 && <StepDocuments nextStep={nextStep} />}

        {stepIndex === 5 && <StepSubscriptions nextStep={nextStep} />}

        {stepIndex === 6 && (
          <StepPayment
            moveToStep={moveToStep}
            displayPaymentError={displayPaymentError}
          />
        )}
      </Stepper>

      {/* <ButtonGroup
        size="xs"
        variant="ghost"
        colorScheme="blackAlpha"
        isAttached
        mt="1rem">
        <Button
          onClick={() => {
            push(
              generateFrontPath({
                id: "@account.dashboard",
                query: { step: 0 },
              }),
              undefined,
              { shallow: true },
            );
          }}>
          Reset
        </Button>

        <Button
          onClick={() => {
            nextStep();
          }}>
          Next step
        </Button>

        <Button
          onClick={() => {
            moveToStep(WIZARD_STEPS_VALUE_PAYMENT);
          }}>
          Payment
        </Button>
      </ButtonGroup> */}
    </>
  );
}

export default Wizard;
